import { TextArea } from 'components/core';
import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { currentDashboardState } from 'states';
import { UI_TEXT } from '../../../../../constants';

import styles from './DashboardTabs.module.css';
import { FieldTooltip } from 'molecules/fieldTooltip/FieldTooltip';

interface ConfigProps {
  register: any;
  setValue: any;
  formState: any;
  isExisting?: boolean;
}

const handleChange = (setValue: any) => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
  setValue('dashboardConfig', e.target.value, { shouldValidate: true });
};

// ── Config field documentation ────────────────────────────────────────────────
const CONFIG_FIELD_DOCS = [
  {
    field: 'title',
    label: 'title',
    description: 'The dashboard title shown in the config. Usually matches the Info tab title.',
    example: '"title": "Pre Service Dashboard"',
  },
  {
    field: 'chartTitle',
    label: 'chartTitle',
    description: 'An optional override title for charts within this dashboard configuration.',
    example: '"chartTitle": "Registration Overview"',
  },
  {
    field: 'filter',
    label: 'filter',
    description:
      'Additional dashboard-level filters applied to all charts. Leave empty if global filters from the Filters tab are sufficient.',
    example: '"filter": ""',
  },
  {
    field: 'isDecimal',
    label: 'isDecimal',
    description: 'Whether metric values on this dashboard should display decimal points.',
    example: '"isDecimal": false',
  },
];

export const Config = ({ register, setValue, formState, isExisting = false }: ConfigProps) => {
  const currentDashboard = useRecoilValue(currentDashboardState);

  useEffect(() => {
    if (!isExisting) {
      setValue('dashboardConfig', UI_TEXT.DASHBOARD_TEMPLATES.DASHBOARD_CONFIG);
    }
  }, [currentDashboard]);
  const { errors } = formState;

  return (
    <div style={{ paddingBottom: '40px' }} className="col-sm-12 col-md-12 col-lg-8">
      <div className="col-sm-12 col-md-9 col-lg-9 mt-4 mb-3">
        {/* Field reference */}
        <div className={styles.fieldRefPanel} style={{ marginBottom: 20 }}>
          <div className={styles.fieldRefHeader}>
            <span className="material-icons" style={{ fontSize: 15, color: 'var(--primary-1)' }}>
              menu_book
            </span>
            <span className={styles.fieldRefTitle}>Config field reference</span>
            <span className={styles.fieldRefSub}>
              Hover over a field name for help, or click to keep it open
            </span>
          </div>
          <div className={styles.fieldRefGrid}>
            {CONFIG_FIELD_DOCS.map((doc) => (
              <div key={doc.field} className={styles.fieldRefItem}>
                <div className={styles.fieldRefItemTop}>
                  <code className={styles.fieldRefCode}>{doc.label}</code>
                  <FieldTooltip
                    title={doc.label}
                    description={doc.description}
                    example={doc.example}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Config textarea */}
        <div className="mb-4">
          <div className={styles.fieldLabelRow}>
            <span className={styles.fieldLabel}>Dashboard configuration</span>
            <FieldTooltip
              title="Dashboard configuration"
              description="A JSON object that controls additional behaviour of the dashboard — such as display settings, chart-level overrides, and filter defaults. Most dashboards can leave this empty unless specific behaviour changes are needed."
              example='{"title": "Pre Service Dashboard", "filter": "", "isDecimal": false}'
              note="Leave empty if no special configuration is needed. Consult the technical team before adding new config keys."
            />
          </div>
          <div className={styles.fieldHint}>Controls additional behaviour of this dashboard.</div>
          {!isExisting && (
            <div className={styles.jsonNote}>
              <span className="material-icons" style={{ fontSize: 16, flexShrink: 0 }}>
                info
              </span>
              <strong>
                {' '}
                New dashboard — the JSON below is a starter template. Edit it before saving.
              </strong>
            </div>
          )}
          <TextArea
            rows={10}
            showTitle={false}
            showDescription={false}
            title=""
            {...register('dashboardConfig')}
            className={`${errors.dashboardConfig ? 'is-invalid' : ''}`}
            onChange={handleChange(setValue)}
          />
          {errors.dashboardConfig && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {errors.dashboardConfig.message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};